import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Brain, Heart, Calendar, Plus, ChevronRight } from 'lucide-react';
import { API } from '../../App';

const RoundsHistoryPage = () => {
  const [rounds, setRounds] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await API.get('/locker/rounds');
        setRounds(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="rounds-history-page">
      <div className="bg-[#1A2E1D] text-white">
        <div className="container mx-auto px-6 md:px-10 py-4 max-w-4xl flex items-center justify-between">
          <Link to="/locker" className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm transition-colors" data-testid="back-to-locker">
            <ArrowLeft className="w-4 h-4" /> Locker
          </Link>
          <Link to="/locker/round/new" className="inline-flex items-center gap-2 bg-[#BC562B] text-white px-4 py-2 text-sm font-semibold hover:bg-[#9A4520] transition-colors" data-testid="new-round-btn">
            <Plus className="w-4 h-4" /> New round review
          </Link>
        </div>
      </div>

      <main className="container mx-auto px-6 md:px-10 py-10 max-w-4xl">
        <div className="mb-8">
          <p className="text-[#BC562B] text-xs uppercase tracking-[0.25em] mb-2">Round Reviews</p>
          <h1 className="font-['Playfair_Display'] text-3xl text-[#1A1A1A]">Your unified rounds</h1>
          <p className="text-[#57534E] mt-2 text-sm">Each review captures Awareness, Acceptance & Action for one round — and surfaces "The Gap".</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-[#2C5530] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : rounds.length === 0 ? (
          <div className="text-center py-20 bg-white border border-[#E7E5E4]" data-testid="empty-rounds">
            <Calendar className="w-12 h-12 text-[#E7E5E4] mx-auto mb-4" />
            <p className="text-[#737373] mb-4">No round reviews yet</p>
            <Link to="/locker/round/new" className="inline-flex items-center gap-2 bg-[#2C5530] text-white px-5 py-2.5 font-semibold hover:bg-[#1E3A21] transition-colors">
              <Plus className="w-4 h-4" /> Log your first round review
            </Link>
          </div>
        ) : (
          <ul className="space-y-3">
            {rounds.map((r) => (
              <li key={r.id} className="bg-white border border-[#E7E5E4] hover:border-[#2C5530] transition-colors" data-testid={`round-row-${r.id}`}>
                <Link to={`/locker/rounds/${r.id}`} className="flex items-center justify-between p-5 gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-1.5">
                      <span className="font-semibold text-[#1A1A1A]">{r.course_name || 'Round'}</span>
                      {r.round_type && <span className="text-[10px] font-semibold uppercase tracking-wider bg-[#F2F0E9] text-[#57534E] px-2 py-0.5">{r.round_type}</span>}
                      {r.score != null && <span className="text-[10px] font-semibold uppercase tracking-wider bg-[#1A2E1D]/10 text-[#1A2E1D] px-2 py-0.5">Score {r.score}</span>}
                    </div>
                    <p className="text-xs text-[#737373] mb-2">{new Date(r.round_date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}</p>
                    <div className="flex flex-wrap gap-3 text-xs">
                      {r.overall_awareness != null && (
                        <span className="inline-flex items-center gap-1 text-[#2C5530] font-semibold"><Brain className="w-3.5 h-3.5" /> {r.overall_awareness}/10</span>
                      )}
                      {r.overall_acceptance != null && (
                        <span className="inline-flex items-center gap-1 text-[#BC562B] font-semibold"><Heart className="w-3.5 h-3.5" /> {r.overall_acceptance}/10</span>
                      )}
                      {r.overall_action != null && (
                        <span className="inline-flex items-center gap-1 text-[#1A2E1D] font-semibold"><ArrowRight className="w-3.5 h-3.5" /> {r.overall_action}/10</span>
                      )}
                      {r.gap_aa != null && (
                        <span className="inline-flex items-center gap-1 text-[#737373] font-semibold border-l border-[#E7E5E4] pl-3">
                          Gap {r.gap_aa > 0 ? `+${r.gap_aa}` : r.gap_aa}
                        </span>
                      )}
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-[#A8A29E] flex-shrink-0" />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </main>
    </div>
  );
};

export default RoundsHistoryPage;
